import {
  useFeatureGate,
  useDynamicConfig as useStatsigDynamicConfig,
  useExperiment as useStatsigExperiment,
} from '@statsig/react-bindings';
import { TFunction } from 'i18next';
import { Provider as JotaiProvider } from 'jotai';
import { DevTools as JotaiDevTools, useAtomsDebugValue } from 'jotai-devtools';
import { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';

import { useI18nReact, useTranslate } from '@growtherapy/i18n';
import { SproutI18nProvider } from '@growtherapy/sprout-ui';

import {
  SeedlingDependenciesContextProvider as BaseSeedlingDependenciesContextProvider,
  FormSubmitted,
  SeedlingDependencies,
  UserClicked,
  UserFocused,
  UserInputted,
  UserViewed,
} from '@grow-therapy-team/seedling-components';

import { AuthedApolloProvider } from './apollo';
import { Auth0Provider } from './auth';
import { initBranch } from './branch/initBranch';
import { GrowBranding } from './components/branding/GrowBranding';
import { logger } from './datadog/logger';
import { ErrorPage } from './error/ErrorPage';
import { initI18n } from './i18n/config';
import { Routes } from './nav';
import { useBackForwardCacheReload } from './nav/useBackForwardCacheReload';
import { useTrackEvent } from './segment/segment';
import { appJotaiStore } from './state/jotai';
import { ExperimentProvider } from './statsig/ExperimentProvider';
import { Statsig, StatsigProvider } from './statsig/StatsigProvider';
import { toast } from './toasts';

// Initialize i18n translations
initI18n();

// Initialize Branch.io for smart banners
initBranch();

// Support for React Devtools
function DebugAtoms() {
  useAtomsDebugValue();
  return null;
}

const useFeatureFlag: NonNullable<SeedlingDependencies['statsig']>['useFeatureFlag'] = (flagName: string) => {
  const { value } = useFeatureGate(flagName);
  return {
    isLoading: false, // Feature gates are not loading in this context
    hasFeature: value,
  };
};

const useExperiment: NonNullable<SeedlingDependencies['statsig']>['useExperiment'] = (experimentName: string) => {
  const experiment = useStatsigExperiment(experimentName);
  return {
    isLoading: false, // Experiments are not loading in this context
    experiment: experiment,
  };
};

const useDynamicConfig: NonNullable<SeedlingDependencies['statsig']>['useDynamicConfig'] = (configKey: string) => {
  const config = useStatsigDynamicConfig(configKey);
  return {
    isLoading: false,
    config: config,
  };
};

export type SeedlingDependenciesContextProviderProps = PropsWithChildren<{
  t: TFunction;
}>;

function SeedlingDependenciesContextProvider({ t, children }: SeedlingDependenciesContextProviderProps) {
  const { formSubmitted, userClicked, userFocused, userInputted, userViewed } = useTrackEvent();
  const i18n = useI18nReact();
  return (
    <BaseSeedlingDependenciesContextProvider
      value={{
        t,
        i18n,
        logger,
        segment: {
          userInputted: userInputted as UserInputted,
          formSubmitted: formSubmitted as FormSubmitted,
          userClicked: userClicked as UserClicked,
          userFocused: userFocused as UserFocused,
          userViewed: userViewed as UserViewed,
        },
        toast: {
          success: toast.success as NonNullable<SeedlingDependencies['toast']>['success'],
          error: toast.error as NonNullable<SeedlingDependencies['toast']>['error'],
        },
        statsig: {
          checkGate: Statsig.checkGate,
          useFeatureFlag,
          useExperiment,
          useDynamicConfig,
        },
      }}
    >
      {children}
    </BaseSeedlingDependenciesContextProvider>
  );
}

function App() {
  useBackForwardCacheReload();
  const t = useTranslate();

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <JotaiProvider store={appJotaiStore}>
        {/* Rest assured that the below dev tools are not available in the prod build */}
        <JotaiDevTools />
        <DebugAtoms />
        <StatsigProvider>
          <BrowserRouter>
            <Auth0Provider>
              <SproutI18nProvider t={t}>
                <SeedlingDependenciesContextProvider t={t}>
                  <AuthedApolloProvider>
                    <ExperimentProvider>
                      <Routes />
                    </ExperimentProvider>
                    <GrowBranding />
                  </AuthedApolloProvider>
                </SeedlingDependenciesContextProvider>
              </SproutI18nProvider>
            </Auth0Provider>
          </BrowserRouter>
        </StatsigProvider>
      </JotaiProvider>
    </ErrorBoundary>
  );
}

export default App;
